/**
 * @deprecated This context is deprecated in favor of the Zustand store.
 * Use useDrawerStore from 'stores/drawerStore' instead.
 *
 * Migration guide:
 * - Before: const { openDrawer, setOpenDrawer, handleOpenDrawerFilter } = useDrawerState();
 * - After: const openDrawer = useDrawerStore(state => state.openDrawer);
 *         const setOpenDrawer = useDrawerStore(state => state.setOpenDrawer);
 *         const handleOpenDrawerFilter = useDrawerStore(state => state.handleOpenDrawerFilter);
 *
 * For multiple values, use shallow comparison:
 * - import { useShallow } from 'zustand/react/shallow';
 * - const { openDrawer, activeFilter } = useDrawerStore(
 *     useShallow(state => ({ openDrawer: state.openDrawer, activeFilter: state.activeFilter }))
 *   );
 *
 * Files using this context (for migration reference):
 * - src/components/Helper/Filter/index.tsx
 *   Uses: isOpenMenus, setIsOpenMenus, openDrawer, setOpenDrawer, openDrawerFilter,
 *         setOpenDrawerFilter, setOpenDrawerCopilot, openDrawerAdvisor, openDrawerMentorship,
 *         setOpenDrawerAdvisor, openDrawerScheduledCalls, activeFilter, setActiveFilter,
 *         translateXDrawerFilters, setTranslateXDrawerFilters, openDrawerAccessHub,
 *         setOpenDrawerAccessHub, openDrawerMarketplace, setOpenDrawerMarketplace,
 *         handleOpenMenus, handleOpenDrawerAdvisor, handleOpenDrawerMentorship,
 *         handleActiveFilter, handleActiveAdvisor, handleActiveMentorship,
 *         handleActiveMarketplace, handleActiveAccessHub, handleOpenDrawerScheduledCalls,
 *         handleOpenDrawerAccessHub, handleOpenDrawerMarketplace
 *
 * - src/components/Helper/FloatMenu/index.tsx
 *   Uses: translateXDrawerFilters
 *
 * - src/contexts/index.tsx
 *   Uses: DrawerProvider (wraps application)
 *
 * Note: src/components/Helper/Filter/hooks/useDrawerState.ts is a separate local hook,
 * not related to this context. Consider consolidating or removing it.
 *
 * @see stores/drawerStore.ts for the new Zustand-based implementation
 * @see types/drawer.ts for shared type definitions
 */
import React, { createContext, useContext, useState, ReactNode } from 'react';

import { FilterItem, PlanFeatures, FilterChangeHandler } from 'types';

interface DrawerContextProps {
  isOpenMenus: boolean;
  setIsOpenMenus: (open: boolean) => void;
  openDrawer: boolean;
  setOpenDrawer: (open: boolean) => void;
  openDrawerFilter: boolean;
  setOpenDrawerFilter: (open: boolean) => void;
  openDrawerCopilot: boolean;
  setOpenDrawerCopilot: (open: boolean) => void;
  openDrawerAdvisor: boolean;
  setOpenDrawerAdvisor: (open: boolean) => void;
  openDrawerMentorship: boolean;
  setOpenDrawerMentorship: (open: boolean) => void;
  openDrawerAccessHub: boolean;
  setOpenDrawerAccessHub: (open: boolean) => void;
  openDrawerMarketplace: boolean;
  setOpenDrawerMarketplace: (open: boolean) => void;
  activeFilter: FilterItem | null;
  setActiveFilter: (filter: FilterItem | null) => void;
  activePlanFeatures: PlanFeatures | string[] | null;
  setActivePlanFeatures: (features: PlanFeatures | string[] | null) => void;
  translateXDrawerFilters: string;
  setTranslateXDrawerFilters: (value: string) => void;
  handleOpenMenus: () => void;
  handleOpenDrawerFilter: () => void;
  handleOpenDrawerCopilot: () => void;
  handleOpenDrawerAdvisor: () => void;
  handleOpenDrawerMentorship: () => void;
  handleOpenDrawerAccessHub: () => void;
  handleOpenDrawerMarketplace: () => void;
  openDrawerScheduledCalls: boolean;
  setOpenDrawerScheduledCalls: (open: boolean) => void;
  handleOpenDrawerScheduledCalls: () => void;
  handleActiveFilter: FilterChangeHandler;
  handleActiveFilterCopilot: FilterChangeHandler;
  handleActiveAdvisor: (activeItem: FilterItem | null) => void;
  handleActiveMentorship: (activeItem: FilterItem | null) => void;
  handleActiveMarketplace: (activeItem: FilterItem | null) => void;
  handleActiveAccessHub: (activeItem: FilterItem | null) => void;
}

interface DrawerProviderProps {
  children: ReactNode;
}

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined);

/**
 * @deprecated Use useDrawerStore from 'stores/drawerStore' instead.
 * This provider will be removed in a future release.
 *
 * The Zustand store provides the same functionality without the need for a provider wrapper.
 * Simply import and use useDrawerStore directly in your components.
 */
export const DrawerProvider: React.FC<DrawerProviderProps> = ({ children }) => {
  const [isOpenMenus, setIsOpenMenus] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openDrawerFilter, setOpenDrawerFilter] = useState(false);
  const [openDrawerCopilot, setOpenDrawerCopilot] = useState(false);
  const [openDrawerAdvisor, setOpenDrawerAdvisor] = useState(false);
  const [openDrawerMarketplace, setOpenDrawerMarketplace] = useState(false);
  const [openDrawerAccessHub, setOpenDrawerAccessHub] = useState(false);
  const [openDrawerMentorship, setOpenDrawerMentorship] = useState(false);
  const [openDrawerScheduledCalls, setOpenDrawerScheduledCalls] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterItem | null>(null);
  const [activePlanFeatures, setActivePlanFeatures] = useState<PlanFeatures | string[] | null>([]);
  const [translateXDrawerFilters, setTranslateXDrawerFilters] = useState('0px');

  const handleOpenMenus = () => {
    setIsOpenMenus(!isOpenMenus);
  };

  const handleOpenDrawerFilter = () => {
    setOpenDrawer(true);
    setOpenDrawerFilter(true);
    setOpenDrawerCopilot(false);
    setOpenDrawerAdvisor(false);
    setOpenDrawerMentorship(false);
    setOpenDrawerScheduledCalls(false);
    setActiveFilter(null);
    setOpenDrawerAccessHub(false);
    setOpenDrawerMarketplace(false);
    setTranslateXDrawerFilters('88px');
  };

  const handleOpenDrawerCopilot = () => {
    setOpenDrawer(true);
    setOpenDrawerFilter(false);
    setOpenDrawerCopilot(true);
    setOpenDrawerAdvisor(false);
    setOpenDrawerMentorship(false);
    setOpenDrawerScheduledCalls(false);
    setActiveFilter(null);
    setOpenDrawerAccessHub(false);
    setOpenDrawerMarketplace(false);
    setTranslateXDrawerFilters('88px');
  };

  const handleOpenDrawerAdvisor = () => {
    setOpenDrawer(true);
    setOpenDrawerFilter(false);
    setOpenDrawerCopilot(false);
    setOpenDrawerAdvisor(true);
    setOpenDrawerMentorship(false);
    setOpenDrawerScheduledCalls(false);
    setActiveFilter(null);
    setOpenDrawerAccessHub(false);
    setOpenDrawerMarketplace(false);
    setTranslateXDrawerFilters('88px');
  };

  const handleOpenDrawerMarketplace = () => {
    setOpenDrawer(true);
    setOpenDrawerFilter(false);
    setOpenDrawerCopilot(false);
    setOpenDrawerAdvisor(false);
    setOpenDrawerMentorship(false);
    setOpenDrawerScheduledCalls(false);
    setActiveFilter(null);
    setOpenDrawerAccessHub(false);
    setOpenDrawerMarketplace(true);
    setTranslateXDrawerFilters('88px');
  };

  const handleOpenDrawerAccessHub = () => {
    setOpenDrawer(true);
    setOpenDrawerFilter(false);
    setOpenDrawerCopilot(false);
    setOpenDrawerAdvisor(true);
    setOpenDrawerMentorship(false);
    setOpenDrawerScheduledCalls(false);
    setActiveFilter(null);
    setOpenDrawerAccessHub(true);
    setOpenDrawerMarketplace(false);
    setTranslateXDrawerFilters('88px');
  };

  const handleOpenDrawerMentorship = () => {
    setOpenDrawer(true);
    setOpenDrawerFilter(false);
    setOpenDrawerCopilot(false);
    setOpenDrawerAdvisor(false);
    setOpenDrawerMentorship(true);
    setOpenDrawerScheduledCalls(false);
    setActiveFilter(null);
    setOpenDrawerAccessHub(false);
    setOpenDrawerMarketplace(false);
    setTranslateXDrawerFilters('88px');
  };

  const handleOpenDrawerScheduledCalls = () => {
    setOpenDrawer(true);
    setOpenDrawerFilter(false);
    setOpenDrawerCopilot(false);
    setOpenDrawerAdvisor(false);
    setOpenDrawerMentorship(false);
    setOpenDrawerScheduledCalls(true);
    setOpenDrawerAccessHub(false);
    setOpenDrawerMarketplace(false);
    setActiveFilter({
      name: 'Chamadas Agendadas',
      icon: () => null,
      enabled: true,
    });
    setTranslateXDrawerFilters('548px');
  };

  const handleActiveFilter: FilterChangeHandler = (activeItem, fetchMessages) => {
    setActiveFilter(activeItem);
    setTranslateXDrawerFilters('378px');
    if (activeItem?.type && fetchMessages) {
      fetchMessages(activeItem.type);
    }
  };

  const handleActiveFilterCopilot: FilterChangeHandler = (activeItem, fetchMessages) => {
    setActiveFilter(activeItem);
    setTranslateXDrawerFilters('470px');
    if (activeItem?.type && fetchMessages) {
      fetchMessages(activeItem.type);
    }
  };

  const handleActiveAdvisor = (activeItem: FilterItem | null) => {
    setActiveFilter(activeItem);
    setTranslateXDrawerFilters('548px');
  };

  const handleActiveMentorship = (activeItem: FilterItem | null) => {
    setActiveFilter(activeItem);
    setTranslateXDrawerFilters('548px');
  };

  const handleActiveMarketplace = (activeItem: FilterItem | null) => {
    setActiveFilter(activeItem);
    setTranslateXDrawerFilters('548px');
  };

  const handleActiveAccessHub = (activeItem: FilterItem | null) => {
    setActiveFilter(activeItem);
    setTranslateXDrawerFilters('548px');
  };

  return (
    <DrawerContext.Provider
      value={{
        isOpenMenus,
        setIsOpenMenus,
        openDrawer,
        setOpenDrawer,
        openDrawerFilter,
        setOpenDrawerFilter,
        openDrawerCopilot,
        setOpenDrawerCopilot,
        openDrawerAdvisor,
        openDrawerMentorship,
        setOpenDrawerAdvisor,
        setOpenDrawerMentorship,
        openDrawerScheduledCalls,
        setOpenDrawerScheduledCalls,
        activeFilter,
        setActiveFilter,
        activePlanFeatures,
        setActivePlanFeatures,
        translateXDrawerFilters,
        setTranslateXDrawerFilters,
        openDrawerAccessHub,
        setOpenDrawerAccessHub,
        openDrawerMarketplace,
        setOpenDrawerMarketplace,
        handleOpenMenus,
        handleOpenDrawerFilter,
        handleOpenDrawerCopilot,
        handleOpenDrawerAdvisor,
        handleOpenDrawerMentorship,
        handleActiveFilter,
        handleActiveFilterCopilot,
        handleActiveAdvisor,
        handleActiveMentorship,
        handleOpenDrawerScheduledCalls,
        handleOpenDrawerAccessHub,
        handleOpenDrawerMarketplace,
        handleActiveAccessHub,
        handleActiveMarketplace,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};

/**
 * @deprecated Use useDrawerStore from 'stores/drawerStore' instead.
 *
 * Migration example:
 * ```tsx
 * // Before (Context)
 * const { openDrawer, handleOpenDrawerFilter } = useDrawerState();
 *
 * // After (Zustand) - single value
 * const openDrawer = useDrawerStore(state => state.openDrawer);
 * const handleOpenDrawerFilter = useDrawerStore(state => state.handleOpenDrawerFilter);
 *
 * // After (Zustand) - multiple values with shallow comparison
 * import { useShallow } from 'zustand/react/shallow';
 * const { openDrawer, activeFilter } = useDrawerStore(
 *   useShallow(state => ({ openDrawer: state.openDrawer, activeFilter: state.activeFilter }))
 * );
 * ```
 *
 * Benefits of Zustand:
 * - No provider wrapper needed
 * - Automatic state persistence (with persist middleware)
 * - DevTools integration for debugging
 * - More granular subscriptions (only re-render when specific values change)
 * - Derived selectors for computed values (selectIsAnyDrawerOpen, selectActiveDrawerType)
 *
 * @see stores/drawerStore.ts
 */
export const useDrawerState = (): DrawerContextProps => {
  const context = useContext(DrawerContext);
  if (context === undefined) {
    throw new Error('useDrawerState must be used within a DrawerProvider');
  }
  return context;
};
